import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CheckedCombo.css';
import { markUserEdit } from '../hooks/useUnsavedGuard';

/**
 * Shared CHECKED COMBO — a combobox whose list carries checkboxes, so several
 * values can be picked at once.
 *
 * Use it wherever one record legitimately covers more than one master-data
 * value (a DOC that qualifies an analyst on several test codes and methods, for
 * example). It is deliberately a *closed* list: callers pass the options, and
 * the user can only ever pick from them, so free-text variants of the same
 * value can never enter the data.
 *
 * Sibling: `features/modules/QueryCombo` is the SERVER-DRIVEN variant of this
 * control — same interaction, but it lazily loads a column's distinct values
 * and cascades against the other filters. This one takes a plain option list.
 * If a third variant is ever needed, refactor QueryCombo onto this base rather
 * than adding another.
 *
 * The popup is portaled to <body> so it is never clipped by a dialog's own
 * `overflow` — the record dialog scrolls its audit trail internally, and an
 * in-flow dropdown would be cut off at the card edge.
 */
export interface CheckedComboProps {
  /** The closed list of pickable values. */
  options: string[];
  /** Currently ticked values. */
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  /** Renders the required-field tint (BO UI Standard §4). */
  required?: boolean;
  disabled?: boolean;
  /** Shows "Loading…" in place of the list while options are being fetched. */
  loading?: boolean;
  ariaLabel?: string;
  /** Word used in the "N <unit> selected" summary, e.g. "tests". */
  unit?: string;
  /** OPTIONAL — when set, the trigger shows EVERY selected value joined by this
   *  separator (e.g. "; ") instead of collapsing to "N selected" past two picks.
   *  Truncates with an ellipsis when it overflows; the full list is on hover.
   *  Mirrors the legacy XAF editor that lists all names in the box. Omit to keep
   *  the compact "N selected" summary. */
  summaryJoin?: string;
  /** OPTIONAL — paginate the (filtered) list at this many rows per page and show
   *  a numbered pager (‹ 1 2 3 … N › + "Page X of N (M items)") at the bottom,
   *  mirroring the legacy XAF CheckedListBoxEditor. Omit (default) to keep the
   *  plain scrolling list — every existing usage is unchanged. Search still
   *  filters first; pagination applies to the filtered result. */
  pageSize?: number;
  /** OPTIONAL — hide the "OK" button in the tools row (the popup still closes on
   *  click-away / Escape). Matches the legacy checked-list which has no OK. */
  hideOk?: boolean;
  /** OPTIONAL — when nothing is selected, show the available-option count in the
   *  trigger, e.g. "All client (2)". Pair with cascaded option lists so the number
   *  reflects what's still pickable. Omit to keep the plain placeholder. */
  showCount?: boolean;
  /** Alternate the list rows white / warm-sand (--ai-row-zebra). Opt-in. */
  zebra?: boolean;
  /** OPTIONAL — map a stored VALUE to the text shown for it (list row, trigger
   *  summary, and the search match). Omit to display the value itself. Use when
   *  the value is a key/Oid but the user should see a name — e.g. a contact Oid
   *  rendered as its e-mail address (Project To / CC). */
  labelOf?: (value: string) => string;
  /** OPTIONAL — render the dropdown caret as the welded ORANGE block the other
   *  combos (AiCombo / LookupCombo) use, instead of the muted grey chevron. For a
   *  field that must match a legacy XAF orange-caret dropdown (e.g. Project To / CC). */
  caretAccent?: boolean;
}

export function CheckedCombo({
  options, values, onChange, placeholder = 'Select…',
  required, disabled, loading, ariaLabel, unit = 'selected', pageSize, summaryJoin, hideOk, showCount, zebra, labelOf, caretAccent,
}: CheckedComboProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  /** Keyboard-cursor item index within the current page (‑1 = no cursor yet). */
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 220, maxHeight: 320 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const openPanel = () => {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Flip above the trigger when there isn't room below — measured against the
      // nearest MODAL/dialog, not just the viewport, so the list never spills BELOW
      // a centered popup and looks detached from it (user report 2026-09-30). The
      // popup is also height-capped to the room it has, so it stays inside.
      const modal = btnRef.current?.closest('[role="dialog"],[aria-modal="true"],.stx-modal') as HTMLElement | null;
      const m = modal?.getBoundingClientRect();
      const clipBottom = m ? Math.min(window.innerHeight, m.bottom) : window.innerHeight;
      const clipTop = m ? Math.max(0, m.top) : 0;
      const below = clipBottom - r.bottom;
      const above = r.top - clipTop;
      const openUp = below < 220 && above > below;
      const room = (openUp ? above : below) - 10;
      // A usable list needs ~140px. When NEITHER side of the trigger has that
      // much - a short dialog - the panel spans the clip box rather than
      // growing past it, so it never spills outside its own dialog.
      const fits = room >= 140;
      const maxHeight = fits
        ? Math.min(380, room)
        : Math.max(120, Math.min(380, clipBottom - clipTop - 16));
      const top = fits
        ? (openUp ? r.top - maxHeight - 4 : r.bottom + 2)
        : Math.max(clipTop + 8, Math.min(r.bottom + 2, clipBottom - maxHeight - 8));
      setPos({
        top: top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        maxHeight,
      });
    }
    setQuery('');
    setPage(0);
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    // Marked handled, so a dialog this list sits in (AppDialog) closes only the
    // list, not itself (user report 2026-10-20: Esc dropped the whole picker).
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Filtering changes the result set, so jump back to the first page.
  useEffect(() => { setPage(0); }, [query]);

  // Reset the keyboard cursor on open / filter / page change; keep it in view.
  useEffect(() => { setActive(-1); }, [open, query, page]);
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector('.ckc-opt.active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  // Optional VALUE→display-text map (e.g. a contact Oid shown as its e-mail);
  // identity when unset, so every existing usage is unchanged.
  const label = (v: string): string => (labelOf ? labelOf(v) : v);
  const shown = query
    ? options.filter(o => label(o).toLowerCase().includes(query.toLowerCase()))
    : options;

  // Optional pagination (legacy CheckedListBoxEditor parity). Search filters
  // first; the pager walks the filtered result.
  const paging = !!pageSize && pageSize > 0;
  const pageCount = paging ? Math.max(1, Math.ceil(shown.length / pageSize!)) : 1;
  const curPage = Math.min(page, pageCount - 1);
  const listed = paging ? shown.slice(curPage * pageSize!, (curPage + 1) * pageSize!) : shown;
  const pageWindow = () => {
    const maxBtns = 7;
    let start = Math.max(0, curPage - Math.floor(maxBtns / 2));
    const end = Math.min(pageCount, start + maxBtns);
    start = Math.max(0, end - maxBtns);
    return Array.from({ length: end - start }, (_, i) => start + i);
  };

  const toggle = (v: string) => {
    markUserEdit(btnRef.current);
    const set = new Set(values);
    if (set.has(v)) set.delete(v); else set.add(v);
    // Kept in the OPTION list's order, not click order, so the same selection
    // always renders (and hashes) identically.
    onChange(options.filter(o => set.has(o)).concat(
      [...set].filter(v2 => !options.includes(v2))));
  };

  const allShownSelected = shown.length > 0 && shown.every(v => values.includes(v));

  const summary = values.length === 0
    ? (showCount ? `${placeholder} (${options.length})` : placeholder)
    : summaryJoin != null ? values.map(label).join(summaryJoin)
    : values.length <= 2 ? values.map(label).join(', ')
    : `${values.length} ${unit}`;

  return (
    <>
      <button
        ref={btnRef} type="button" disabled={disabled}
        aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open}
        className={`ckc-trigger${required ? ' req' : ''}${disabled ? ' disabled' : ''}${values.length ? ' ckc-has' : ''}${caretAccent ? ' ckc-accent' : ''}`}
        onClick={openPanel}
        title={summaryJoin != null
          ? (values.length ? values.map(label).join(summaryJoin) : undefined)
          : (values.length > 2 ? values.map(label).join(', ') : undefined)}
      >
        <span className={values.length ? 'ckc-val' : 'ckc-ph'}>
          {loading ? 'Loading…' : summary}
        </span>
        {caretAccent
          ? <span className="ckc-chev" aria-hidden="true"><i className="ckc-tri" /></span>
          : <i className="ti ti-chevron-down ckc-chev" />}
      </button>

      {open && createPortal(
        <>
          <div className="ckc-backdrop" onMouseDown={() => setOpen(false)} />
          <div className={`ckc-pop${zebra ? " ckc-pop--zebra" : ""}`} role="listbox" aria-multiselectable="true"
            style={{ top: pos.top, left: pos.left, minWidth: Math.max(220, pos.width), maxHeight: pos.maxHeight }}>
            <div className="ckc-search">
              <i className="ti ti-search" />
              <input autoFocus value={query} placeholder="Type to filter…"
                onChange={e => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // ↓/↑ move a cursor through the (paged) list, Home/End jump, Enter
                  // toggles the cursor item and STAYS open (multi-select), Esc closes
                  // (mouse-free, ADR-0013, user request 2026-09-21). Space is left to
                  // the filter text — Enter is the toggle key here.
                  if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a < 0 ? 0 : Math.min(listed.length - 1, a + 1))); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a < 0 ? listed.length - 1 : Math.max(0, a - 1))); }
                  else if (e.key === 'Home') { e.preventDefault(); setActive(listed.length ? 0 : -1); }
                  else if (e.key === 'End') { e.preventDefault(); setActive(listed.length - 1); }
                  else if (e.key === 'Enter') { const v = listed[active]; if (v != null) { e.preventDefault(); toggle(v); } }
                  // Esc closes and returns focus to the trigger so keyboard nav
                  // keeps working / the dialog restores to its opener (2026-09-21).
                  else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); btnRef.current?.focus(); }
                }} />
            </div>
            <div className="ckc-tools">
              <button type="button" className="ckc-tool" disabled={allShownSelected}
                onClick={() => { markUserEdit(btnRef.current); onChange([...new Set([...values, ...shown])]); }}>
                Select all
              </button>
              <button type="button" className="ckc-tool red" disabled={values.length === 0}
                onClick={() => { markUserEdit(btnRef.current); onChange([]); }}>Clear</button>
              <span style={{ flex: 1 }} />
              <span className="ckc-count">{values.length} selected</span>
              {!hideOk && (
                <button type="button" className="ckc-ok" onClick={() => { setOpen(false); btnRef.current?.focus(); }}>OK</button>
              )}
            </div>
            <div className="ckc-list" ref={listRef}>
              {loading && <div className="ckc-empty">Loading…</div>}
              {!loading && shown.length === 0 && <div className="ckc-empty">No matching values</div>}
              {!loading && listed.map((v, i) => (
                <label key={v} className={`ckc-opt${values.includes(v) ? ' sel' : ''}${i === active ? ' active' : ''}`} title={label(v)}>
                  <input type="checkbox" checked={values.includes(v)} onChange={() => toggle(v)} />
                  <span>{label(v)}</span>
                </label>
              ))}
            </div>
            {!loading && paging && shown.length > pageSize! && (
              <div className="ckc-pager">
                <button type="button" className="ckc-pg" disabled={curPage === 0}
                  onClick={() => setPage(curPage - 1)} aria-label="Previous page">‹</button>
                {pageWindow().map(p => (
                  <button key={p} type="button"
                    className={`ckc-pg${p === curPage ? ' active' : ''}`}
                    onClick={() => setPage(p)}>{p + 1}</button>
                ))}
                <button type="button" className="ckc-pg" disabled={curPage >= pageCount - 1}
                  onClick={() => setPage(curPage + 1)} aria-label="Next page">›</button>
                <span className="ckc-pageinfo">Page {curPage + 1} of {pageCount} ({shown.length} items)</span>
              </div>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
